import React from "react";
//#region types
import { GraphConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
//#endregion
//#region components
import { ChartContainer } from "components/common";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import ReportWidgetType = WintSuperkollAPIModel.ReportWidgetType;
//#endregion
import { DateHelper, LocalizationHelperInstance, QueryStateHelper, UrlHelper } from "utils/helpers";
import { FormFieldConstants } from "utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { TranslationKeys } from "translations";
import { AccountingCategoryType } from "../../../models/enums/accounting";

const SalesBarChart: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const labelFormatter = (label?: string) => (label ? label.slice(-2).replace("-", "0") : "");

  const bookingDateEncoder = QueryStateHelper.dateRangeQueryEncoder(
    FormFieldConstants.Accounting.CompanyBooksReportFilter.BookingDate
  );
  const categoryIdEncoder = QueryStateHelper.numberQueryEncoder(
    FormFieldConstants.Accounting.CompanyBooksReportFilter.CategoryId
  );
  const categoryEncoder = QueryStateHelper.stringQueryEncoder(
    FormFieldConstants.Accounting.CompanyBooksReportFilter.Category
  );

  const datasetRangesRef = React.useRef<{ start: Date; end: Date }[]>([]);

  const salesCategoryId = AccountingCategoryType.Sales;

  return (
    <ChartContainer
      useSkeleton={ReportWidgetType.Bar}
      id={"current_month_sales"}
      chartAPIKey={GraphConstants.ChartKeys.DashboardSalesComparableBarWidget}
      chartCacheKey={`${GraphConstants.ChartKeys.DashboardSalesComparableBarWidget}-type-${ChartEnums.ChartDataFrequency.Daily}`}
      filter={{ type: ChartEnums.ChartDataFrequency.Daily }}
      labelFormatter={labelFormatter}
      useClickableChart
      onChartSuccessHandler={(data) => {
        datasetRangesRef.current =
          data?.data?.map((d) => ({ start: new Date(d.startDate as any), end: new Date(d.endDate as any) })) ?? [];
      }}
      onPeriodClick={({ datasetIndex, label }) => {
        const ranges = datasetRangesRef.current;
        const range = (datasetIndex != null ? ranges[datasetIndex] : undefined) ?? ranges[0];
        if (!range) return;

        const day = typeof label === "string" ? Number.parseInt(label, 10) : Number(label ?? NaN);
        if (!Number.isFinite(day)) return;

        const startMonthLastDay = DateHelper.getLastDayOfMonth(range.start).getDate();
        const endMonthLastDay = DateHelper.getLastDayOfMonth(range.end).getDate();
        const startDay = Math.min(day, startMonthLastDay);
        const endDay = Math.min(day, endMonthLastDay);

        const start = new Date(range.start.getFullYear(), range.start.getMonth(), startDay, 0, 0, 0, 0);
        const end = new Date(range.end.getFullYear(), range.end.getMonth(), endDay, 23, 59, 59, 999);

        const params = new URLSearchParams("");
        bookingDateEncoder.urlEncode(params, undefined, { start, end });
        categoryIdEncoder.urlEncode(params, undefined, salesCategoryId);
        categoryEncoder.urlEncode(
          params,
          undefined,
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Sale)
        );
        navigate(`${UrlHelper.formatPath(`/Reports/Accounting`)}?${params.toString()}`, {
          state: { background: location },
        });
      }}
      yAxisTickUnit={ChartEnums.YAxisTickUnitType.Currency}
      overrideLabelFontSize={10}
    />
  );
};

export default SalesBarChart;
